import {
  Col,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
} from "antd";
import { Rule } from "antd/lib/form";
import TextArea from "antd/lib/input/TextArea";
import { areaApi } from "api/area.api";
import { notificationApi } from "api/notification.api";
import { productApi } from "api/product.api";
import { shopApi } from "api/shop.api";
import React, { useEffect, useRef, useState } from "react";
import { Area } from "types/area";
import { ModalStatus } from "types/modal";
import { News } from "types/news";
import { NotificationMode, NotificationType } from "types/notification";
import { Shop } from "types/shop";
import { Notification } from "types/notification";
import { debounce } from "lodash";
import { newsApi } from "api/news.api";

const rules: Rule[] = [{ required: true, message: "Bắt buộc" }];

const { Option } = Select;

interface NotificationForm extends Notification {
  areaId?: number;
  shopId?: number;
  newsId?: number;
}

export const NotificationModal = ({
  visible,
  status,
  notification,
  onClose,
  onSubmitOk,
}: {
  visible: boolean;
  status: ModalStatus;
  notification: Partial<Notification>;
  onClose: () => void;
  onSubmitOk: () => void;
}) => {
  const [form] = Form.useForm<NotificationForm>();
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState<Area[]>([]);
  const [news, setNews] = useState<News[]>([]);
  const [products, setProducts] = useState([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [isFetchingShop, setIsFetchingShop] = useState(false);

  useEffect(() => {
    if (status == "create" && visible) {
      form.resetFields();
      form.setFieldsValue({
        mode: NotificationMode.Global,
      });
    }
  }, [visible, status]);

  useEffect(() => {
    form.setFieldsValue({
      ...notification,
      areaId: notification?.area?.id,
      shopId: notification?.shop?.id,
      newsId: notification?.news?.id,
    });

    if (notification.news) {
      setNews([notification.news]);
    }
    if (notification.shop) {
      setShops([notification.shop]);
    }
    return () => {};
  }, [notification]);

  useEffect(() => {
    fetchAreas();
    fetchShops();
    fetchNews();
  }, []);

  const fetchNews = async (search = "") => {
    const res = await newsApi.findAll({ page: 1, limit: 50, search });
    setNews(res.data.newses);
  };

  const fetchProducts = async (search = "") => {
    const res = await productApi.findAll({ page: 1, limit: 50, search });
    setProducts(res.data.products);
  };

  const fetchShops = async (search = "") => {
    const res = await shopApi.findAll({ page: 1, limit: 50, search });
    setShops(res.data.shops);
  };

  const fetchAreas = async () => {
    const res = await areaApi.findAll({ page: 1, limit: 0 });
    setAreas(res.data.areas);
  };

  const createData = async () => {
    const valid = await form.validateFields();
    const data = {
      notification: form.getFieldsValue(),
      areaId: form.getFieldValue("areaId"),
      newsId: form.getFieldValue("newsId"),
      shopId: form.getFieldValue("shopId"),
    };

    setLoading(true);
    try {
      const res = await notificationApi.create(data);
      message.success("Tạo mới thành công");
      onClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const updateData = async () => {
    const valid = await form.validateFields();
    const data = {
      notification: form.getFieldsValue(),
      areaId: form.getFieldValue("areaId"),
      newsId: form.getFieldValue("newsId"),
      shopId: form.getFieldValue("shopId"),
    };
    setLoading(true);
    try {
      const res = await notificationApi.update(notification?.id || 0, data);
      message.success("Cập nhật thành công");
      onClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const onSearchShop = useRef(debounce(fetchShops, 500));

  const onSearchNews = useRef(debounce(fetchNews, 500));

  return (
    (<Modal
      onCancel={onClose}
      open={visible}
      title={status == "create" ? "Tạo mới thông báo" : "Cập nhật thông báo"}
      style={{ top: 20 }}
      width={600}
      okText="Lưu"
      cancelText="Đóng"
      confirmLoading={loading}
      onOk={() => {
        status == "create" ? createData() : updateData();
      }}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Tiêu đề" name="title" rules={rules}>
              <Input placeholder="" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Nội dung" name="content" rules={rules}>
              <TextArea rows={5} maxLength={255} showCount />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Đối tượng nhận thông báo"
              name="mode"
              rules={rules}
            >
              <Radio.Group>
                <Radio value={NotificationMode.Global}>Tất cả khách hàng</Radio>
                {/* <Radio value={NotificationMode.Range}>
                  Khách hàng theo khu vực
                </Radio> */}
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item shouldUpdate noStyle>
              {() =>
                form.getFieldValue("mode") == NotificationMode.Range && (
                  <Form.Item label="Khu vực" name="areaId" rules={rules}>
                    <Select defaultValue="" style={{ width: "100%" }}>
                      <Select.Option value={0}>Tất cả</Select.Option>
                      {areas.map((e) => (
                        <Select.Option value={e.id} key={e.id}>
                          {e.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )
              }
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Loại thông báo" name="type" rules={rules}>
              <Radio.Group>
                <Radio value={NotificationType.Normal}>Thông thường</Radio>
                <Radio value={NotificationType.News}>Tin tức</Radio>
                <Radio value={NotificationType.Shop}>Doanh nghiệp</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item shouldUpdate noStyle>
              {() => {
                switch (form.getFieldValue("type")) {
                  case NotificationType.News:
                    return (
                      <Form.Item label="Tin tức" name="newsId" rules={rules}>
                        <Select
                          onSearch={onSearchNews.current}
                          showSearch
                          notFoundContent={
                            isFetchingShop ? <Spin size="small" /> : null
                          }
                          filterOption={false}
                          style={{ width: "100%" }}
                        >
                          {news.map((e) => (
                            <Option value={e.id}>{e.title}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );

                  case NotificationType.Shop:
                    return (
                      <Form.Item
                        label="Doanh nghiệp"
                        name="shopId"
                        rules={rules}
                      >
                        <Select
                          onSearch={onSearchShop.current}
                          showSearch
                          notFoundContent={
                            isFetchingShop ? <Spin size="small" /> : null
                          }
                          filterOption={false}
                          style={{ width: "100%" }}
                        >
                          {shops.map((e) => (
                            <Option value={e.id}>{e.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                }
              }}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>)
  );
};
